import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PlusIcon, StorefrontIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useGetBookingTypes } from '@/api/booking-types'
import {
  Service,
  ServiceFields,
  useCreateService,
  useCreateSku,
  useUpdateService,
} from '@/api/services'
import { AvatarFormField, SelectFormField } from '@/components/form'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import ServiceIcon from '@/components/Icons'
import { Button } from '@/components/ui/button'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { SelectOption } from '@/components/ui/select'
import { toastFactory } from '@/factories'
import { SERVICE_COLORS, SERVICE_ICONS } from '@/helpers/constants'
import { getAssetLink } from '@/helpers/get-asset-link'
import { useDirtyFormGuard, useFormSave } from '@/hooks'
import { getApiErrorMessage } from '@/utils/error'

import { FormChangeFields, getFormChanges } from '../../shared/form-changes'
import { PersonFormLayout } from '../../shared/person-form-layout'
import { SwitchRow } from '../../shared/switch-row'
import { UpdatedAt } from '../../shared/updated-at'
import { ColorField, IconField } from '../../shared/visual-fields'
import {
  NO_TRAINING_TYPE_CATEGORIES,
  SERVICE_CATEGORY_LIST,
  SERVICE_DESCRIPTION_MAX_LENGTH,
  SERVICE_FORM_WIDTH,
  SERVICE_NAME_MAX_LENGTH,
} from '../services.constants'
import { getBookingTypeRef, getCategoryLabel } from '../services.utils'
import {
  getServiceFormDefaults,
  makeServiceFormSchema,
  SERVICE_IMAGE_PATH,
  ServiceFormData,
} from './service-form-modal.constants'

interface ServiceFormModalProps {
  category?: string
  duplicate?: boolean
  service?: Service
  onClose: VoidFunction
  onSaved?: (service: { category: string; id: number }) => void
}

const getImageUrl = (file: string | null | undefined) =>
  file ? getAssetLink({ file, path: SERVICE_IMAGE_PATH }) : undefined

export const ServiceFormModal = ({
  category,
  duplicate = false,
  service,
  onClose,
  onSaved,
}: ServiceFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { mutateAsync: createService } = useCreateService()
  const { mutateAsync: updateService } = useUpdateService()
  const { mutateAsync: createSku } = useCreateSku()
  const { data: bookingTypes = [] } = useGetBookingTypes()

  const isEdit = !!service && !duplicate

  const schema = useMemo(() => makeServiceFormSchema(t), [t])

  const form = useForm<ServiceFormData>({
    defaultValues: getServiceFormDefaults(service, { category, duplicate }),
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isDirty, isSubmitting } = form.formState
  const selectedCategory = form.watch('category')
  const color = form.watch('color')
  const needsVisuals = !NO_TRAINING_TYPE_CATEGORIES.includes(selectedCategory)

  const categoryOptions = useMemo<SelectOption<string>[]>(
    () =>
      SERVICE_CATEGORY_LIST.map((value) => ({
        label: getCategoryLabel(t, value),
        value,
      })),
    [t],
  )

  const bookingTypeOptions = useMemo<SelectOption<number>[]>(() => {
    const options = bookingTypes.map(({ id, name }) => ({ label: name, value: id }))
    const current = service ? getBookingTypeRef(service) : null
    // Note: a booking type that was deleted since still has to show, or the field would look empty
    if (current && !options.some(({ value }) => value === current.id)) {
      options.push({ label: current.name, value: current.id })
    }
    return options
  }, [bookingTypes, service])

  const changeFields = useMemo<FormChangeFields<ServiceFormData>>(
    () => ({
      active: {
        format: (value) => t(value ? 'common:booleans.yes' : 'common:booleans.no'),
        label: t('organisation:servicesPage.service.fields.active'),
      },
      activeType: {
        format: (value) => t(value ? 'common:booleans.yes' : 'common:booleans.no'),
        label: t('organisation:servicesPage.service.fields.activeType'),
      },
      bookingType: {
        format: (value) =>
          bookingTypeOptions.find((option) => option.value === value)?.label ??
          t('organisation:servicesPage.service.fields.bookingType.placeholder'),
        label: t('organisation:servicesPage.service.fields.bookingType.label'),
      },
      category: {
        format: (value) => getCategoryLabel(t, String(value)),
        label: t('organisation:servicesPage.service.fields.category.label'),
      },
      color: { label: t('organisation:servicesPage.service.fields.color.label') },
      description: { label: t('organisation:servicesPage.service.fields.description.label') },
      icon: { label: t('organisation:servicesPage.service.fields.icon.label') },
      image: { image: true, label: t('organisation:servicesPage.service.fields.image.label') },
      imageInCategory: {
        image: true,
        label: t('organisation:servicesPage.service.fields.imageInCategory.label'),
      },
      internalName: { label: t('organisation:servicesPage.service.fields.internalName.label') },
      name: { label: t('organisation:servicesPage.service.fields.name.label') },
    }),
    [bookingTypeOptions, t],
  )

  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const onSubmit: SubmitHandler<ServiceFormData> = async (data) => {
    const fields: ServiceFields = {
      active: data.active,
      activeType: data.activeType,
      bookingType: data.bookingType ?? '',
      category: data.category,
      color: data.color,
      description: data.description.trim(),
      icon: data.icon,
      image: data.image?.[0],
      imageInCategory: data.imageInCategory?.[0],
      internalName: data.internalName.trim(),
      name: data.name.trim(),
      // TODO: legacy always sent `true` here (a broken category check) - kept as is, see BACKEND_IMPROVEMENTS.md
      personal: true,
    }

    try {
      if (isEdit && service) {
        await updateService({ ...fields, id: service.id })
        toastFactory.success(t('organisation:servicesPage.service.toasts.updated'))
        onSaved?.({ category: data.category, id: service.id })
      } else {
        const created = await createService({
          ...fields,
          duplicateId: duplicate ? service?.id : undefined,
        })

        // Note: the BE copies only the images of a duplicated service, so its subservices are copied here
        const copyResults = duplicate
          ? await Promise.allSettled(
              (service?.sku ?? []).map(({ cost, duration, name, quantity }) =>
                createSku({
                  cost: Number(cost),
                  duration: duration ?? undefined,
                  name,
                  productId: created.id,
                  quantity: quantity ?? undefined,
                }),
              ),
            )
          : []

        if (copyResults.some(({ status }) => status === 'rejected')) {
          toastFactory.error(t('organisation:servicesPage.service.toasts.duplicatedPartly'))
        } else {
          toastFactory.success(
            t(
              duplicate
                ? 'organisation:servicesPage.service.toasts.duplicated'
                : 'organisation:servicesPage.service.toasts.created',
            ),
          )
        }
        onSaved?.({ category: data.category, id: created.id })
      }
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const title = duplicate
    ? t('organisation:servicesPage.service.modal.titleDuplicate')
    : isEdit
      ? t('organisation:servicesPage.service.modal.titleEdit')
      : t('organisation:servicesPage.service.modal.titleNew')

  const submit = useFormSave({ form, isEdit, onSubmit, onUnchanged: onClose })

  return (
    <Modal
      autoWidth
      title={title}
      headerIcon={StorefrontIcon}
      footer={
        <>
          {isEdit && <UpdatedAt value={service?.updatedAt} />}
          <Button use="secondary" onClick={handleClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button loading={isSubmitting} use="primary" onClick={submit}>
            {isEdit ? t('common:actions.save') : t('common:actions.add')}
            <Icon icon={isEdit ? CheckIcon : PlusIcon} />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout form={form} width={SERVICE_FORM_WIDTH} onSubmit={submit}>
        <AvatarFormField
          existingImageUrl={getImageUrl(service?.imageInCategory)}
          label={t('organisation:servicesPage.service.fields.imageInCategory.label')}
          name="imageInCategory"
          uploadLabel={t('organisation:servicesPage.service.fields.imageInCategory.upload')}
        />
        <AvatarFormField
          existingImageUrl={getImageUrl(service?.image)}
          label={t('organisation:servicesPage.service.fields.image.label')}
          name="image"
          uploadLabel={t('organisation:servicesPage.service.fields.image.upload')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:servicesPage.service.fields.name.label')}
            maxLength={SERVICE_NAME_MAX_LENGTH}
            name="name"
            placeholder={t('organisation:servicesPage.service.fields.name.placeholder')}
          />
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:servicesPage.service.fields.internalName.label')}
            maxLength={SERVICE_NAME_MAX_LENGTH}
            name="internalName"
            placeholder={t('organisation:servicesPage.service.fields.internalName.placeholder')}
          />
        </Grid>

        <IconField
          color={color}
          icons={SERVICE_ICONS as string[]}
          label={t('organisation:servicesPage.service.fields.icon.label')}
          renderIcon={(icon) => <ServiceIcon fill="currentColor" icon={icon} />}
          required={needsVisuals}
        />
        <ColorField
          colors={SERVICE_COLORS as string[]}
          label={t('organisation:servicesPage.service.fields.color.label')}
          required={needsVisuals}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <SelectFormField<ServiceFormData, string>
            withReservedFooter
            required
            label={t('organisation:servicesPage.service.fields.category.label')}
            name="category"
            options={categoryOptions}
            size="medium"
            type="single"
          />
          <SelectFormField<ServiceFormData, number>
            withReservedFooter
            label={t('organisation:servicesPage.service.fields.bookingType.label')}
            name="bookingType"
            options={bookingTypeOptions}
            placeholder={t('organisation:servicesPage.service.fields.bookingType.placeholder')}
            size="medium"
            type="single"
            onClear={() =>
              form.setValue('bookingType', null, { shouldDirty: true, shouldTouch: true })
            }
          />
        </Grid>

        <TextAreaFormField
          withReservedFooter
          required
          label={t('organisation:servicesPage.service.fields.description.label')}
          maxLength={SERVICE_DESCRIPTION_MAX_LENGTH}
          name="description"
          placeholder={t('organisation:servicesPage.service.fields.description.placeholder')}
          rows={3}
        />

        <SwitchRow label={t('organisation:servicesPage.service.fields.active')} name="active" />
        <SwitchRow
          label={t('organisation:servicesPage.service.fields.activeType')}
          name="activeType"
        />
      </PersonFormLayout>
    </Modal>
  )
}
